import { format } from 'date-fns';
import { stateAbbreviations } from './stateAbbreviations';

export const standardizeLocationName = (value) => {
  switch (value) {
    case 'fns':
      return 'FNS';
    case 'grip_tx':
      return 'GRIP_TX';
    case 'sierra':
      return 'SIERRA';
    case 'neesvigs':
      return 'NEESVIGS';
    case 'burris_md':
      return 'BURRIS_MD';
    case 'jax':
      return 'JAX';
    case 'icecube':
      return 'ICECUBE';
    case 'grip_mi':
      return 'GRIP_MI';
    case 'grip_fl':
      return 'GRIP_FL';
    case 'grip_nj':
      return 'GRIP_NJ';
    default:
      return value;
  }
};

export const getBoxType = (subscription) => {
  const boxType = JSON.stringify(subscription.properties).includes('no_pasta_box')
    ? 'no_pasta_box'
    : 'curated_box';

  if (subscription.sku && subscription.sku.includes('WGB_CU')) {
    return 'custom_box';
  }
  return boxType;
};

// "6 items (most popular) / Variety Box | $99" → "Standard". Mirrors the
// bundle app's planDisplayLabel so every surface names plans the same way.
export const planLabelFromSubscription = (subscription) => {
  const variantTitle = String(subscription?.variant_title || '');
  const afterSlash = variantTitle.includes('/') ? variantTitle.split('/')[1] : '';
  const raw = afterSlash.split('|')[0].replace(/\bbox\b/i, '').trim();
  const lc = raw.toLowerCase();
  if (!lc) return '';
  if (lc.includes('variety') || lc.includes('mixed')) return 'Standard';
  if (lc.includes('plant') || lc.includes('vegan')) return 'Vegan';
  if (lc.includes('protein')) return 'Protein';
  if (lc.includes('gluten')) return 'Gluten-Free';
  return raw;
};

// "6-pack Standard box" from the subscription alone (item count parsed from
// the variant title). Falls back to the product title when the variant
// carries no count.
export const describePlan = (subscription) => {
  const match = String(subscription?.variant_title || '').match(/(\d+)\s*items?/i);
  const label = planLabelFromSubscription(subscription);
  if (!match) return subscription?.product_title || null;
  return label ? `${match[1]}-pack ${label} box` : `${match[1]}-pack box`;
};

export const getRechargeSubstitutions = (subscription) => {
  let rechargeSubstitutions = [];
  if (!subscription) {
    return rechargeSubstitutions;
  }

  for (var prop of subscription.properties) {
    if (prop.name.includes(`WG_`) && prop.value?.includes(`WG_`)) {
      rechargeSubstitutions.push({ item: prop.name, replacement: prop.value });
    }
  }

  return rechargeSubstitutions;
};

// Parse a charge date to a local Date without the UTC day-shift: a date-only
// string ("2026-08-10") would otherwise parse as UTC midnight and render a day
// early in western timezones. Mirrors formatChargeDate's parsing so callers that
// need a different display format still land on the same calendar day.
export const parseChargeDate = (dateString) => {
  if (!dateString) return null;
  let date;
  if (dateString.includes('T')) {
    date = new Date(dateString);
  } else {
    const [year, month, day] = dateString.split('-').map(Number);
    date = new Date(year, month - 1, day);
  }
  return isNaN(date.getTime()) ? null : date;
};

export const formatChargeDate = (dateString) => {
  if (!dateString) return '';

  try {
    let date;
    
    // Handle ISO date strings (e.g., "2024-01-15T00:00:00Z")
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      // Handle simple date strings (e.g., "2024-01-15")
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date value:', dateString);
      return dateString.split('T')[0];
    }
    
    const currentYear = new Date().getFullYear();
    const formatString = date.getFullYear() === currentYear ? 'EEE, MMM d' : 'EEE, MMM d, yyyy';
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error, 'Input:', dateString);
    return dateString.split('T')[0];
  }
};

  export const formatChargeDateForAPI = (dateString) => {
    if (!dateString) return '';
  
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      // date.setDate(date.getDate() + 1); // Add one day
  
      const formattedMonth = (date.getMonth() + 1).toString().padStart(2, '0');
      const formattedDay = date.getDate().toString().padStart(2, '0');
      const formattedYear = date.getFullYear();
  
      return `${formattedMonth}.${formattedDay}.${formattedYear}`;
    } catch (error) {
      console.error('Error formatting charge date for API:', error);
      return '';
    }
  };

export const getStateAbbreviation = (stateName) => {
  if (!stateName) return '';
  
  // Handle case where the input is already an abbreviation
  if (stateName.length === 2 && stateName === stateName.toUpperCase()) {
    return stateName;
  }

  // Normalize the state name (lowercase and trim)
  const normalizedStateName = stateName.toLowerCase().trim();
  
  return stateAbbreviations[normalizedStateName] || stateName;
};

/**
 * The next business day after `date` (skips weekends). Used wherever we need
 * the soonest date an order can be charged and still ship the same week.
 *
 * @param {Date} date
 * @returns {Date}
 */
export const getNextBusinessDay = (date = new Date()) => {
  const nextDay = new Date(date);
  do {
    nextDay.setDate(nextDay.getDate() + 1);
  } while (nextDay.getDay() === 0 || nextDay.getDay() === 6);
  return nextDay;
};

// States/territories we cannot reliably ship frozen goods to.
const RESTRICTED_PROVINCES = ['AK', 'HI', 'PR', 'ALASKA', 'HAWAII', 'PUERTO RICO'];

/**
 * Whether a province name or code is somewhere we can't ship frozen goods.
 * Accepts either the abbreviation ("AK") or the full name ("Alaska").
 *
 * @param {string} province
 * @returns {boolean}
 */
export const isRestrictedProvince = (province) => {
  if (!province) return false;
  return RESTRICTED_PROVINCES.includes(province.toString().trim().toUpperCase());
};

// States where a "click to cancel" law requires the compliant cancellation
// flow (cancel option continuously visible, not gated behind reasons/offers):
// - CA: Automatic Renewal Law (§ 17602)
// - NY: NYC "Click to Cancel" rule (DCWP, effective Oct 1, 2026). Applied to
//   all of NY since addresses only carry state-level data.
const CLICK_TO_CANCEL_STATES = ['CA', 'NY'];

/**
 * Check whether a Recharge shipping address is in a state with a
 * "click to cancel" law. Used to enable the compliant cancellation flow.
 *
 * @param {Object} address - A Recharge address object (province / province_code)
 * @returns {boolean}
 */
export const isClickToCancelAddress = (address) => {
  if (!address) return false;
  const province = (address.province_code || address.province || '').toString().trim();
  if (!province) return false;
  return CLICK_TO_CANCEL_STATES.includes(getStateAbbreviation(province).toUpperCase());
};

export const getExtendedFulfillmentLocation = (location) => {
  switch (location) {
    case 'fns':
      return 'FNS';
    // Grip_TX (Denton) took over the FNS lane in Aug 2026 — products are
    // tagged YES_GRIP_TX / NO_GRIP_TX / PORTAL_HIDE_GRIP_TX.
    case 'grip_tx':
      return 'GRIP_TX';
    case 'sierra':
      return 'SIERRA';
    case 'neesvigs':
      return 'NEESVIGS';
    case 'burris_md':
      return 'BURRIS_MD';
    case 'jax':
      return 'JAX';
    case 'icecube':
      return 'ICECUBE';
    case 'grip_mi':
      return 'GRIP_MI';
    case 'grip_fl':
      return 'GRIP_FL';
    case 'grip_nj':
      return 'GRIP_NJ';
    case 'grip_nv':
      return 'GRIP_NV';
    default:
      return location;
  }
};

// ---------------------------
// Gift Card / Non-Subscription Order Helpers
// ---------------------------

/**
 * Check if a Shopify order is a gift card order by inspecting line item titles.
 * Used to skip delivery estimates and filter display for gift card orders.
 *
 * @param {Object} order - A Shopify storefront order object
 * @returns {boolean}
 */
export const isGiftCardOrder = (order) => {
  const lineItems = order?.lineItems?.edges || [];
  return lineItems.some(edge => {
    const title = (edge?.node?.title || '').toLowerCase();
    return title.includes('gift card') || title.includes('giftcard');
  });
};

/**
 * Normalize order tags from Admin (comma string), Storefront/API (array), or helper shape.
 * @param {Object} order
 * @returns {string[]}
 */
const getOrderTags = (order) => {
  const raw = order?.tags;
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t || '').trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(',').map((t) => t.trim()).filter(Boolean);
  }
  return [];
};

/**
 * True when the order has a Wildgrain transit tag like delivered_1 or delivered_4+.
 * @param {Object} order
 * @returns {boolean}
 */
export const orderHasDeliveredTag = (order) =>
  getOrderTags(order).some((tag) => /^delivered_/i.test(tag));

/**
 * True when any successful Shopify fulfillment reports shipment_status "delivered".
 * Supports Admin REST shape and the portal's successfulFulfillments normalization.
 * @param {Object} order
 * @returns {boolean}
 */
export const orderHasDeliveredShipment = (order) => {
  const fromFulfillments = (order?.fulfillments || []).some(
    (f) => String(f?.shipment_status || f?.shipmentStatus || '').toLowerCase() === 'delivered'
  );
  if (fromFulfillments) return true;

  return (order?.successfulFulfillments || []).some(
    (f) => String(f?.shipmentStatus || f?.shipment_status || '').toLowerCase() === 'delivered'
  );
};

/**
 * Multiple ways Wildgrain/Shopify mark an order as delivered. Any one is enough:
 * - delivered_x order tag
 * - fulfillment shipment_status === "delivered"
 * - explicit isDelivered / delivered flag from API helpers
 *
 * @param {Object} order
 * @returns {boolean}
 */
export const isOrderDelivered = (order) => {
  if (!order) return false;
  if (order.isDelivered === true || order.delivered === true) return true;
  if (orderHasDeliveredTag(order)) return true;
  if (orderHasDeliveredShipment(order)) return true;
  return false;
};

/**
 * Check if a Recharge charge should be excluded as a delivery estimate reference.
 * Gift card, gift box, and other onetime-only charges don't have physical delivery tracking,
 * so we only want charges that contain at least one subscription line item.
 *
 * @param {Object} charge - A Recharge charge object
 * @returns {boolean}
 */
export const isNonSubscriptionCharge = (charge) => {
  const lineItems = charge?.line_items || [];
  if (lineItems.length === 0) return false;
  // If every line item is a onetime purchase, this is not a regular subscription charge
  const allOnetimes = lineItems.every(item => item.purchase_item_type === 'onetime');
  if (allOnetimes) return true;
  // Also check titles and properties as a fallback
  return lineItems.some(item => {
    const title = (item.title || '').toLowerCase();
    const properties = item?.properties || [];
    const hasGiftProperties = properties.some(p => (p.name || '').startsWith('__rc_gift'));
    return hasGiftProperties || title.includes('gift card') || title.includes('giftcard');
  });
};

/**
 * Builds a URL to the Recharge hosted payment method portal
 * @param {string} customerHash - The Recharge customer hash
 * @param {string} token - The Recharge API token
 * @returns {string} The complete payment portal URL
 */
export const buildRechargePaymentPortalUrl = (customerHash, token) => {
  if (!customerHash || !token) {
    throw new Error('Customer hash and token are required to build payment portal URL');
  }
  return `https://wildgrain.com/tools/recurring/portal/${customerHash}/payment_methods?token=${token}`;
};

/** True when product title is a dozen / bulk pack line (vs. themed bundle). */
export const isBulkDozenTitle = (product) =>
  (product?.title || '').toLowerCase().includes('dozen');

/**
 * Opens the Recharge payment portal in a new tab using portal access API
 * @param {Function} fetchPortalAccess - The function to fetch portal access from Recharge API
 * @throws {Error} If portal access fetch fails
 */
export const navigateToRechargePaymentPortal = async (fetchPortalAccess) => {
  try {
    console.log('Fetching portal access...');
    const portalAccess = await fetchPortalAccess('payment_methods');
    console.log('Portal access response:', portalAccess);

    if (!portalAccess || !portalAccess.portal_url) {
      throw new Error('Invalid portal access response');
    }

    console.log('Opening portal URL in new tab:', portalAccess.portal_url);
    window.open(portalAccess.portal_url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Error fetching portal access:', error);
    throw error;
  }
};